/**
 * services/apiService.js
 *
 * Centralized API service that automatically includes
 * Keycloak authentication tokens in all requests.
 */

import axios from "axios";
import { getAccessToken, refreshAccessToken } from "@/plugins/auth";

const BASE_URL = window.RUNTIME_CONFIG.VUE_APP_API_URL || "";

// Create an axios instance with default configuration
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.warn("Could not get access token:", error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh on 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we got a 401 and haven't retried yet, try refreshing the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error(
          "Token refresh failed, redirecting to login:",
          refreshError
        );
        // Token refresh failed, could redirect to login
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Requests
export { apiClient };
export default apiClient;
